import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faLightbulb,
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"

type CalloutKind = "tip" | "note" | "warning" | "danger"

const KINDS: CalloutKind[] = ["tip", "note", "warning", "danger"]

const ICONS: Record<CalloutKind, IconDefinition> = {
  tip: faLightbulb,
  note: faCircleInfo,
  warning: faTriangleExclamation,
  danger: faTriangleExclamation,
}

const DEFAULT_TITLES: Record<CalloutKind, string> = {
  tip: "Tip",
  note: "Note",
  warning: "Warning",
  danger: "Careful",
}

interface DocsCalloutProps {
  kind: string
  title?: string
  children: React.ReactNode
}

/**
 * An accented advice block (tip / note / warning / danger). Rendered from a
 * `:::tip` style container directive in the markdown; falls back to "note"
 * for any unrecognised kind.
 */
const DocsCallout: React.FC<DocsCalloutProps> = ({ kind, title, children }) => {
  const safeKind: CalloutKind = KINDS.includes(kind as CalloutKind)
    ? (kind as CalloutKind)
    : "note"
  const heading = title?.trim() || DEFAULT_TITLES[safeKind]

  return (
    <div className={`docs-callout docs-callout--${safeKind}`} role="note">
      <span className="docs-callout__icon" aria-hidden>
        <FontAwesomeIcon icon={ICONS[safeKind]} />
      </span>
      <div className="docs-callout__content">
        <p className="docs-callout__title">{heading}</p>
        <div className="docs-callout__body">{children}</div>
      </div>
    </div>
  )
}

export default DocsCallout
